import { DownloadIcon, IconButton, Tooltip, classNames } from '@movingimage-evp/mi-ui-component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { downloadFile } from '../../../../../../utils';
import { useProgressContext } from '../../../../../providers/progress-context/progress-context';

import defaultStyles from './download-file.module.css';

export type DownloadFileProps = {
  webcastId: string;
  downloadLink?: string | null;
  fileName?: string;
  disabled?: boolean;
  tooltipText?: string;
  alwaysShowTooltip?: boolean;
  label: string;
  buttonTestId: string;
  tooltipTestId: string;
  className?: string;
};

export function DownloadFile({
  webcastId,
  downloadLink,
  fileName,
  disabled,
  tooltipText,
  alwaysShowTooltip,
  label,
  buttonTestId,
  tooltipTestId,
  className,
}: DownloadFileProps) {
  const { addProgressIndicator, updateStatus, updateProgress, cancelProgressIndicator } = useProgressContext();
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);

  const effectiveTooltip = isDownloading ? t('manager.eventSummary.downloadInProgress') : tooltipText || '';

  const effectiveFileName =
    fileName || (downloadLink ? (new URL(downloadLink).searchParams.get('downloadFileName') ?? undefined) : undefined);

  const handleDownload = () => {
    if (!webcastId || !downloadLink) return;

    let downloadRequest: XMLHttpRequest | undefined;

    const progressbarId = `${webcastId}-${Date.now()}`;

    addProgressIndicator({
      id: progressbarId,
      transferType: 'download',
      fileName: effectiveFileName,
      status: 'TRANSFERRING',
      progressPercentage: 0,
      buttons: {
        TRANSFERRING: {
          onButtonClicked: () => {
            downloadRequest?.abort();
            cancelProgressIndicator(progressbarId);
            setIsDownloading(false);
          },
        },
      },
    });

    downloadFile({
      url: downloadLink,
      fileName: effectiveFileName,
      onError: () => {
        updateStatus(progressbarId, 'FAILED');
        setIsDownloading(false);
      },
      onProgress: (percentComplete: number, minutes: number, seconds: number) =>
        updateProgress(progressbarId, percentComplete, { minutes, seconds }),
      onStart: (request: XMLHttpRequest) => {
        downloadRequest = request;
        setIsDownloading(true);
      },
      onSuccess: () => {
        updateStatus(progressbarId, 'FINISHED');
        setIsDownloading(false);
      },
    });
  };

  return (
    <Tooltip
      label={effectiveTooltip}
      hidden={!effectiveTooltip}
      alwaysVisible={alwaysShowTooltip}
      data-testid={tooltipTestId}
    >
      <IconButton
        className={classNames(defaultStyles.downloadButton, className)}
        aria-label={label}
        disabled={disabled || isDownloading || !downloadLink}
        onClick={handleDownload}
        data-testid={buttonTestId}
      >
        <DownloadIcon />
      </IconButton>
    </Tooltip>
  );
}
